import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Search } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Skeleton } from '../components/ui/skeleton';
import { TipoBadge } from '../components/common/Badges';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';
import { errMsg, TIPO_LABELS } from '../lib/format';

const EMPTY = { nombre: '', rfc: '', direccion: '', telefono: '', email: '', tipo: 'construccion_prefab' };

export default function ClientesPage() {
  const queryClient = useQueryClient();
  const { canDelete } = useAuth();
  const [search, setSearch] = useState('');
  const [tipoFilter, setTipoFilter] = useState('todos');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);

  const { data: clientes, isLoading } = useQuery({
    queryKey: ['clients', tipoFilter, search],
    queryFn: async () => {
      const params = {};
      if (tipoFilter !== 'todos') params.tipo = tipoFilter;
      if (search) params.search = search;
      return (await api.get('/clients', { params })).data;
    },
  });

  const saveMutation = useMutation({
    mutationFn: async () => {
      if (editId) return (await api.put(`/clients/${editId}`, form)).data;
      return (await api.post('/clients', form)).data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clients'] });
      toast.success(editId ? 'Cliente actualizado' : 'Cliente creado');
      setDialogOpen(false);
    },
    onError: (e) => toast.error(errMsg(e)),
  });

  const deleteMutation = useMutation({
    mutationFn: async (id) => (await api.delete(`/clients/${id}`)).data,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clients'] });
      toast.success('Cliente eliminado');
    },
    onError: (e) => toast.error(errMsg(e)),
  });

  const openNew = () => { setForm(EMPTY); setEditId(null); setDialogOpen(true); };
  const openEdit = (c) => { setForm({ ...EMPTY, ...c }); setEditId(c.id); setDialogOpen(true); };
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target?.value ?? e }));

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-2xl font-semibold">Clientes</h2>
          <p className="text-sm text-muted-foreground">Administra los clientes por tipo de negocio</p>
        </div>
        <Button onClick={openNew} data-testid="new-client-button">
          <Plus size={16} className="mr-1.5" /> Nuevo cliente
        </Button>
      </div>

      <div className="flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-[220px] max-w-sm">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Buscar cliente..." className="pl-9" data-testid="clients-search-input" />
        </div>
        <Select value={tipoFilter} onValueChange={setTipoFilter}>
          <SelectTrigger className="w-[200px]" data-testid="clients-tipo-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="todos">Todos los tipos</SelectItem>
            {Object.entries(TIPO_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <Card className="border-border shadow-[var(--shadow-elev-1)]">
        <CardContent className="p-0">
          {isLoading ? <div className="p-6 space-y-3">{[...Array(4)].map((_, i) => <Skeleton key={i} className="h-10" />)}</div> : (
            <Table data-testid="clients-table">
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead className="th-label">Nombre</TableHead>
                  <TableHead className="th-label">RFC</TableHead>
                  <TableHead className="th-label">Tipo</TableHead>
                  <TableHead className="th-label">Teléfono</TableHead>
                  <TableHead className="th-label">Email</TableHead>
                  <TableHead className="th-label w-24">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(clientes || []).length === 0 && (
                  <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-8">Sin clientes registrados</TableCell></TableRow>
                )}
                {(clientes || []).map((c) => (
                  <TableRow key={c.id} className="hover:bg-background/40" data-testid={`client-row-${c.id}`}>
                    <TableCell className="font-medium">{c.nombre}</TableCell>
                    <TableCell className="font-mono text-[13px] text-muted-foreground">{c.rfc || '—'}</TableCell>
                    <TableCell><TipoBadge tipo={c.tipo} /></TableCell>
                    <TableCell className="text-muted-foreground">{c.telefono || '—'}</TableCell>
                    <TableCell className="text-muted-foreground">{c.email || '—'}</TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEdit(c)} data-testid={`edit-client-${c.id}`}>
                          <Pencil size={14} />
                        </Button>
                        {canDelete && (
                          <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive hover:text-destructive" onClick={() => deleteMutation.mutate(c.id)} data-testid={`delete-client-${c.id}`}>
                            <Trash2 size={14} />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg" data-testid="client-dialog">
          <DialogHeader><DialogTitle>{editId ? 'Editar cliente' : 'Nuevo cliente'}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2 space-y-1.5">
              <Label>Nombre / Razón social *</Label>
              <Input value={form.nombre} onChange={set('nombre')} data-testid="client-nombre-input" />
            </div>
            <div className="space-y-1.5">
              <Label>RFC</Label>
              <Input value={form.rfc} onChange={set('rfc')} data-testid="client-rfc-input" />
            </div>
            <div className="space-y-1.5">
              <Label>Tipo</Label>
              <Select value={form.tipo} onValueChange={(v) => setForm((f) => ({ ...f, tipo: v }))}>
                <SelectTrigger data-testid="client-tipo-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(TIPO_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Teléfono</Label>
              <Input value={form.telefono} onChange={set('telefono')} data-testid="client-telefono-input" />
            </div>
            <div className="space-y-1.5">
              <Label>Email</Label>
              <Input type="email" value={form.email} onChange={set('email')} data-testid="client-email-input" />
            </div>
            <div className="col-span-2 space-y-1.5">
              <Label>Dirección</Label>
              <Input value={form.direccion} onChange={set('direccion')} data-testid="client-direccion-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
            <Button onClick={() => saveMutation.mutate()} disabled={!form.nombre || saveMutation.isPending} data-testid="save-client-button">
              {editId ? 'Guardar cambios' : 'Crear cliente'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
